// The student's own `getStudentDetails()` response shape isn't pinned down
// anywhere yet, so every getter below tries the handful of key spellings
// used elsewhere in this app (see src/pages/Student/StudentForm) and falls
// back gracefully. buildDetailFields() drops anything still empty so the
// page never renders "undefined" / blank rows for fields this school
// doesn't collect.

const firstTruthy = (...values) => values.find((value) => value !== undefined && value !== null && value !== "");

export const getStudentName = (student) =>
  firstTruthy(
    student?.name,
    student?.full_name,
    [student?.first_name, student?.last_name].filter(Boolean).join(" ").trim() || undefined,
  ) || "";

// Confirmed against the real backend (2026-10-22): profile_image is a bare
// relative storage path (e.g. "APJ-PP/stu_img/...") with no known base URL
// to resolve it against, but s3_image_path alongside it is a ready-to-use,
// pre-signed S3 URL for the same file — that's what's actually displayable.
export const getStudentPhoto = (student) =>
  firstTruthy(student?.s3_image_path, student?.profile_image, student?.photo, student?.photo_url, student?.image) ||
  "";

export const getClassLabel = (student) =>
  firstTruthy(
    // Confirmed against the real backend (2026-10-22): the class name lives
    // at student_program.program.name (e.g. "IX - A"), with the session name
    // alongside it at student_program.session.name (e.g. "2026-27").
    student?.student_program?.program?.name,
    student?.class_name,
    student?.program_name,
    student?.program?.name,
    [student?.class, student?.section].filter(Boolean).join(" - ") || undefined,
  ) || "";

export const getSessionLabel = (student) => firstTruthy(student?.student_program?.session?.name) || "";

export const getAdmissionNumber = (student) =>
  firstTruthy(student?.id_number, student?.admission_no, student?.admission_number) || "";

const getContactNumber = (student) =>
  firstTruthy(student?.phone1, student?.phone_number, student?.mobile, student?.contact_number) || "";

export const formatDateValue = (value) => {
  if (!value) return "";
  // Confirmed against the real backend (2026-10-22): dob comes back as a
  // unix timestamp in seconds, encoded as a string (e.g. "1353325959"), not
  // an ISO date string — new Date() needs that in milliseconds.
  const isUnixSeconds = /^\d+$/.test(String(value));
  const date = isUnixSeconds ? new Date(Number(value) * 1000) : new Date(value);
  if (Number.isNaN(date.getTime())) return String(value);
  return date.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

/** Ordered list of { label, value } pairs for everything the Profile page can show. */
export const buildDetailFields = (student) => {
  if (!student) return [];

  return [
    { label: "Admission Number", value: getAdmissionNumber(student) },
    { label: "Roll No", value: firstTruthy(student.roll_no, student.roll_number) },
    { label: "Class", value: getClassLabel(student) },
    { label: "Gender", value: student.gender },
    { label: "Date of Birth", value: formatDateValue(firstTruthy(student.dob, student.date_of_birth)) },
    { label: "Blood Group", value: student.blood_group },
    { label: "Category", value: student.category },
    { label: "Father's Name", value: student.father_name },
    { label: "Mother's Name", value: student.mother_name },
    { label: "Guardian Name", value: student.guardian_name },
    { label: "Contact Number", value: getContactNumber(student) },
    { label: "Alternate Contact", value: student.phone2 },
    { label: "Email", value: student.email },
    { label: "Address", value: student.address },
  ].filter((field) => firstTruthy(field.value) !== undefined && String(field.value).trim() !== "");
};
